import { Box, Group, Paper, ThemeIcon } from '@mantine/core';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import type { ChartRecordPresentation } from '../utils/chartRecordPresentation';

const ICON_COL_W = 44;

interface ChartRecordCardProps {
  presentation: ChartRecordPresentation;
  /** Omit to render a non-interactive card (no pointer, no hover, not focusable). */
  onActivate?: () => void;
  /**
   * The row is listed but its full details belong to another episode. Still activatable
   * (so the user gets the explanation) but without the clickable hover treatment.
   */
  restricted?: boolean;
  ariaLabel: string;
  children: ReactNode;
}

/**
 * Shared shell for every Client Chart timeline row: tone accent, icon, hover/focus behaviour
 * and the stable `data-record-*` hooks. Record-specific content stays with the caller.
 */
export function ChartRecordCard({
  presentation,
  onActivate,
  restricted = false,
  ariaLabel,
  children,
}: ChartRecordCardProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const { tone, icon: Icon } = presentation;
  const activatable = Boolean(onActivate);
  const clickable = activatable && !restricted;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!onActivate) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onActivate();
  };

  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      role={activatable ? 'button' : undefined}
      tabIndex={activatable ? 0 : undefined}
      aria-label={activatable ? ariaLabel : undefined}
      data-record-type={presentation.recordType}
      data-record-category={presentation.category}
      data-view-mode={presentation.viewMode}
      onClick={onActivate}
      onKeyDown={activatable ? handleKeyDown : undefined}
      onMouseEnter={clickable ? () => setHovered(true) : undefined}
      onMouseLeave={clickable ? () => setHovered(false) : undefined}
      style={{
        cursor: clickable ? 'pointer' : 'default',
        borderLeft: `3px solid var(--mantine-color-${tone}-5)`,
        backgroundColor: hovered ? 'var(--mantine-color-gold-0)' : undefined,
        transition: 'background-color 150ms ease',
      }}
    >
      <Group wrap="nowrap" align="flex-start" gap={0}>
        <Box
          w={ICON_COL_W}
          miw={ICON_COL_W}
          style={{ flexShrink: 0, display: 'flex', justifyContent: 'center' }}
        >
          <ThemeIcon size={34} radius="md" variant="light" color={tone}>
            <Icon size={20} stroke={1.6} />
          </ThemeIcon>
        </Box>
        <Box style={{ flex: 1, minWidth: 0 }}>{children}</Box>
      </Group>
    </Paper>
  );
}
